import React, { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { FiLock } from "react-icons/fi";

/**
 * Stripe's own payment form, mounted on the page.
 *
 * Card numbers and bank details are typed into fields Stripe serves inside an
 * iframe — this site never sees them, never receives them and cannot store
 * them. All it holds is the PaymentIntent's client secret, which is useless on
 * its own.
 *
 * Whether the customer is offered a card, a bank account (ACH), or both is
 * decided by what the receiving business has enabled in Stripe, because the
 * intent is created with automatic payment methods.
 */

/** Cached per publishable key + connected account — loading it twice is waste. */
const stripeCache = new Map();
const getStripe = (publishableKey, stripeAccount) => {
  const key = `${publishableKey}|${stripeAccount || ""}`;
  if (!stripeCache.has(key)) {
    stripeCache.set(
      key,
      loadStripe(publishableKey, stripeAccount ? { stripeAccount } : undefined),
    );
  }
  return stripeCache.get(key);
};

function Spinner() {
  return (
    <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
  );
}

function PaymentFields({ amountLabel, companyName, onPaid, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    onError("");

    // `redirect: "if_required"` keeps the customer on this page for cards,
    // while still allowing the methods that genuinely must leave it (some bank
    // payments ask the customer to sign in to their bank).
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (error) {
      onError(error.message || "That payment could not be completed.");
      setBusy(false);
      return;
    }

    // A bank debit is not settled on the spot — it is accepted now and clears
    // over the next few days, so "processing" is a success, not a failure.
    if (
      paymentIntent &&
      ["succeeded", "processing", "requires_capture"].includes(
        paymentIntent.status,
      )
    ) {
      await onPaid(paymentIntent);
      return;
    }

    onError("That payment was not completed.");
    setBusy(false);
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement onReady={() => setReady(true)} />
      <button
        type="submit"
        disabled={!stripe || !ready || busy}
        className="btn-primary mt-4 w-full disabled:opacity-60"
      >
        {busy ? (
          <>
            <Spinner /> Processing…
          </>
        ) : (
          `Pay ${amountLabel}`
        )}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
        <FiLock size={12} /> Your details are collected by Stripe — never by{" "}
        {companyName}.
      </p>
    </form>
  );
}

export default function StripePaymentForm({
  clientSecret,
  publishableKey,
  stripeAccount,
  amountLabel,
  companyName,
  onPaid,
  onError,
}) {
  const stripePromise = useMemo(
    () => (publishableKey ? getStripe(publishableKey, stripeAccount) : null),
    [publishableKey, stripeAccount],
  );

  if (!publishableKey) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Online payments are not finished being set up for {companyName}. Please
        use one of the other payment methods, or contact them directly.
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: "stripe", variables: { colorPrimary: "#2563eb" } },
      }}
    >
      <PaymentFields
        amountLabel={amountLabel}
        companyName={companyName}
        onPaid={onPaid}
        onError={onError}
      />
    </Elements>
  );
}
